/***
 *
 *   DASHBOARD
 *   Admin quote analytics dashboard.
 *
 **********/

import axios from "axios";
import { Animate, Loader, ViewContext } from "components/lib";
import { useContext, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const COUNT_FORMATTER = new Intl.NumberFormat("en-US");

function getCurrentMonthKey() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

function getMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-").map((value) => Number(value));

  if (!year || !month) {
    return monthKey;
  }

  return MONTH_LABEL_FORMATTER.format(new Date(Date.UTC(year, month - 1, 1)));
}

function getMonthStats(months, monthKey) {
  return months.find((month) => month.month_key === monthKey) || null;
}

function quoteCount(value) {
  return COUNT_FORMATTER.format(value || 0);
}

function getShareValue(part, total) {
  if (!part || !total) {
    return 0;
  }

  return Math.min((part / total) * 100, 100);
}

function Leaderboard({ rows, emptyLabel, accent }) {
  const maxCount =
    rows.reduce(
      (highest, row) => Math.max(highest, row.unique_quotes || 0),
      0,
    ) || 1;

  if (!rows.length) {
    return <div className={styles.emptyState}>{emptyLabel}</div>;
  }

  return (
    <div className={styles.leaderboard}>
      {rows.map((row, index) => {
        const width = `${Math.max((row.unique_quotes / maxCount) * 100, 8)}%`;

        return (
          <div
            className={styles.leaderboardRow}
            key={`${row.account_id}-${index}`}
          >
            <div
              className={`${styles.rankBadge} ${styles[`rankBadge${accent}`]}`}
            >
              {index + 1}
            </div>

            <div className={styles.leaderboardContent}>
              <div className={styles.rowHeader}>
                <div className={styles.rowIdentity}>
                  <div className={styles.agencyName}>
                    {row.account_name || row.account_id}
                  </div>
                  <div className={styles.agencyId}>{row.account_id}</div>
                </div>

                <div className={styles.countPill}>
                  {quoteCount(row.unique_quotes)}
                </div>
              </div>

              <div className={styles.barTrack}>
                <div
                  className={`${styles.barFill} ${styles[`barFill${accent}`]}`}
                  style={{ width }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Dashboard() {
  const [payload, setPayload] = useState();
  const [loading, setLoading] = useState(false);
  const [selectedMonthKey, setSelectedMonthKey] =
    useState(getCurrentMonthKey());
  const context = useContext(ViewContext);

  async function runAPI() {
    setLoading(true);

    await axios
      .get(`/api/company/admin`)
      .then((res) => {
        setPayload(res.data.data);
      })
      .catch(() => {
        context.notification.show(
          "Failed to load dashboard data",
          "error",
          true,
          "toast",
          "x-circle",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    runAPI();
  }, []);

  const metrics = payload?.quote_metrics || {};
  const months = Array.isArray(metrics.months) ? metrics.months : [];
  const allTimeTopAgencies = Array.isArray(metrics?.all_time?.top_agencies)
    ? metrics.all_time.top_agencies
    : [];

  const selectedMonthStats = useMemo(() => {
    const currentStats = getMonthStats(months, selectedMonthKey);

    if (currentStats) {
      return currentStats;
    }

    return {
      month_key: selectedMonthKey,
      month_label: getMonthLabel(selectedMonthKey),
      unique_quotes: 0,
      top_agencies: [],
    };
  }, [months, selectedMonthKey]);

  const monthOptions = useMemo(() => {
    const options = months.map((month) => ({
      ...month,
      month_label: month.month_label || getMonthLabel(month.month_key),
    }));

    if (!options.some((month) => month.month_key === selectedMonthKey)) {
      options.unshift({
        month_key: selectedMonthKey,
        month_label: getMonthLabel(selectedMonthKey),
        unique_quotes: 0,
        top_agencies: [],
      });
    }

    return options.sort((left, right) =>
      right.month_key.localeCompare(left.month_key),
    );
  }, [months, selectedMonthKey]);

  const allTimeCount = metrics?.all_time?.unique_quotes || 0;
  const selectedMonthCount = selectedMonthStats.unique_quotes || 0;
  const selectedMonthShare = getShareValue(selectedMonthCount, allTimeCount);
  const topAgencyName =
    allTimeTopAgencies[0]?.account_name ||
    allTimeTopAgencies[0]?.account_id ||
    "No agencies yet";

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Animate type="pop">
          <div className={styles.page}>
            <section className={styles.hero}>
              <div className={styles.heroGlowOne} />
              <div className={styles.heroGlowTwo} />

              <div className={styles.heroTop}>
                <div className={styles.heroCopy}>
                  <h1 className={styles.title}>Enterprise quote analytics</h1>
                  <p className={styles.subtitle}>
                    Unique quotes count once per quote across all carriers.
                    Monthly views use createdAt calendar months and update from
                    cached summary snapshots.
                  </p>

                  <div className={styles.heroMeta}>
                    <span className={styles.metaPill}>
                      Refresh cadence: ~30 minutes
                    </span>
                    <span className={styles.metaPillMuted}>
                      Leading agency: {topAgencyName}
                    </span>
                  </div>
                </div>

                <div className={styles.controlCard}>
                  <div className={styles.controlEyebrow}>Monthly focus</div>
                  <label className={styles.monthSelectLabel}>
                    <span className={styles.monthSelectText}>Choose month</span>
                    <select
                      className={styles.monthSelect}
                      value={selectedMonthKey}
                      onChange={(event) =>
                        setSelectedMonthKey(event.target.value)
                      }
                    >
                      {monthOptions.map((month) => (
                        <option key={month.month_key} value={month.month_key}>
                          {month.month_label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className={styles.controlCaption}>
                    This filter updates the monthly quote count and monthly top
                    10 agencies.
                  </div>
                </div>
              </div>

              <div className={styles.statsGrid}>
                <article
                  className={`${styles.statCard} ${styles.statCardWarm}`}
                >
                  <div className={styles.statLabel}>All-time quote count</div>
                  <div className={styles.statValue}>
                    {quoteCount(allTimeCount)}
                  </div>
                  <div className={styles.statFoot}>
                    <span className={styles.statFootPrimary}>
                      System-wide unique quotes
                    </span>
                    <span className={styles.statFootSecondary}>
                      All-time view
                    </span>
                  </div>
                </article>

                <article
                  className={`${styles.statCard} ${styles.statCardCool}`}
                >
                  <div className={styles.statHeader}>
                    <div>
                      <div className={styles.statLabel}>
                        Monthly quote count
                      </div>
                      <div className={styles.statValue}>
                        {quoteCount(selectedMonthCount)}
                      </div>
                    </div>
                    <div className={styles.monthBadge}>
                      {selectedMonthStats.month_label}
                    </div>
                  </div>

                  <div className={styles.progressMeta}>
                    <span>
                      {selectedMonthShare.toFixed(1)}% of all-time volume
                    </span>
                    <span>
                      {quoteCount(
                        (selectedMonthStats.top_agencies || []).length,
                      )}{" "}
                      ranked agencies
                    </span>
                  </div>

                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${Math.max(selectedMonthShare, 4)}%` }}
                    />
                  </div>
                </article>
              </div>
            </section>

            <div className={styles.boardGrid}>
              <section className={styles.boardCard}>
                <div className={styles.boardHeader}>
                  <div>
                    <div className={styles.boardEyebrow}>Leaderboard</div>
                    <h2 className={styles.boardTitle}>
                      All-time top 10 agencies
                    </h2>
                  </div>
                  <div
                    className={`${styles.scopeChip} ${styles.scopeChipWarm}`}
                  >
                    All time
                  </div>
                </div>

                <Leaderboard
                  rows={allTimeTopAgencies}
                  emptyLabel="No quote data yet."
                  accent="Warm"
                />
              </section>

              <section className={styles.boardCard}>
                <div className={styles.boardHeader}>
                  <div>
                    <div className={styles.boardEyebrow}>Leaderboard</div>
                    <h2 className={styles.boardTitle}>
                      Monthly top 10 agencies
                    </h2>
                  </div>
                  <div
                    className={`${styles.scopeChip} ${styles.scopeChipCool}`}
                  >
                    {selectedMonthStats.month_label}
                  </div>
                </div>

                <Leaderboard
                  rows={selectedMonthStats.top_agencies || []}
                  emptyLabel="No quote data for this month."
                  accent="Cool"
                />
              </section>
            </div>
          </div>
        </Animate>
      )}
    </>
  );
}
